import { EyeIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { Button } from '@/components/Button';
import { ClickableCount } from '@/components/ClickableCount';
import { Divider } from '@/components/Divider';
import { Loader } from '@/components/Loader';
import { Container } from '@/components/ui';
import { cn } from '@/utils/classname';

import { SeedMonitoringSectionProps } from './types';

/**
 * SeedMonitoringSection - Complete seed monitoring section component
 *
 * Displays seed metrics in a bordered section with:
 * - Section header with title and optional "View" button
 * - Grid of seed type cards (Domains, TLD, IPv4, IPv6, CIDR)
 * - Each card shows Approved/Pending split
 * - Optional clickable counts that trigger navigation callbacks
 * - Loading states with skeleton UI
 *
 * @example
 * // Basic usage with navigation
 * <SeedMonitoringSection
 *   metrics={seedMetrics}
 *   onViewAll={() => navigate('/seeds')}
 *   onSeedClick={(type, status) => navigate(`/seeds?type=${type}&status=${status}`)}
 * />
 *
 * @example
 * // Read-only mode (no actions)
 * <SeedMonitoringSection
 *   metrics={seedMetrics}
 *   showAction={false}
 * />
 */
export const SeedMonitoringSection: React.FC<SeedMonitoringSectionProps> = ({
  metrics,
  isLoading = false,
  onViewAll,
  onSeedClick,
  title = 'Seed Monitoring',
  actionLabel = 'View Seeds',
  showAction = true,
  className,
}) => {
  return (
    <Container variant="emphasized" className={cn(className)}>
      {/* Section Header */}
      <div className="mb-4 flex items-center">
        <h3 className="text-xl font-bold">{title}</h3>
        {showAction && onViewAll && (
          <Button
            className="ml-auto"
            size="xs"
            styleType="rounded"
            startIcon={<EyeIcon className="size-5" />}
            label={actionLabel}
            onClick={onViewAll}
          />
        )}
      </div>

      {/* Seed Type Grid */}
      <div className="grid grid-cols-3 gap-4">
        {metrics.map(metric => (
          <SeedCard
            key={metric.type}
            label={metric.label}
            approved={metric.approved}
            pending={metric.pending}
            isLoading={isLoading}
            onApprovedClick={
              onSeedClick
                ? () => onSeedClick(metric.type, 'approved')
                : undefined
            }
            onPendingClick={
              onSeedClick
                ? () => onSeedClick(metric.type, 'pending')
                : undefined
            }
          />
        ))}
      </div>
    </Container>
  );
};

/**
 * Internal component for individual seed type card
 */
const SeedCard: React.FC<{
  label: string;
  approved: number;
  pending: number;
  isLoading: boolean;
  onApprovedClick?: () => void;
  onPendingClick?: () => void;
}> = ({
  label,
  approved,
  pending,
  isLoading,
  onApprovedClick,
  onPendingClick,
}) => {
  return (
    <Container variant="emphasized" className="relative min-w-32 p-4">
      {/* Label Badge */}
      <h6 className="bg-chariot-brand-primary absolute -top-3 left-4 rounded-sm px-2 py-1 text-xs font-bold text-white uppercase">
        {label}
      </h6>

      {/* Metrics */}
      <Loader className="min-h-14 min-w-14" isLoading={isLoading}>
        <div className="grid grid-cols-2 gap-2 font-bold">
          <span className="text-chariot-text-secondary text-[10px]">
            Approved
          </span>
          <span className="text-chariot-text-secondary text-[10px]">
            Pending
          </span>
          <Divider className="col-span-2 h-[1px]" />
          {onApprovedClick || onPendingClick ? (
            <>
              {onApprovedClick ? (
                <ClickableCount count={approved} onClick={onApprovedClick} />
              ) : (
                <span>{approved}</span>
              )}
              {onPendingClick ? (
                <ClickableCount count={pending} onClick={onPendingClick} />
              ) : (
                <span>{pending}</span>
              )}
            </>
          ) : (
            <>
              <span>{approved}</span>
              <span>{pending}</span>
            </>
          )}
        </div>
      </Loader>
    </Container>
  );
};
